/**
 * The bank accounts, and the reconciliation.
 *
 * Three ideas are drawn rather than explained in a tooltip:
 *
 *   An account number is shown as `****6789` everywhere. Revealing it is
 *   a button that says what it is going to do — "this read is recorded
 *   against your name" — because a control the operator does not know
 *   about is a control they will resent when it turns up in an audit, and
 *   one they do know about is one they behave around.
 *
 *   The reconciliation's difference is the biggest number on the screen
 *   and it stays red until it is zero. The Complete button is disabled
 *   while it is not, and the API refuses anyway (BANK-6) — the disabled
 *   button is a courtesy, not the control.
 *
 *   Outstanding checks and deposits in transit are shown together with
 *   the arithmetic that links them to the book balance, because that
 *   identity is the report an auditor asks for and a screen that shows
 *   only the list makes them do the sum themselves.
 */

import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { useApi, useMutation } from '../lib/useApi.ts';
import { useSession } from '../lib/session.tsx';
import { Card, Empty, ErrorBanner, Loading, Money, Status, Tile } from '../components/ui.tsx';
import { date as fmtDate, dateTime, parseMoney } from '../lib/format.ts';

interface BankAccount {
  id: string;
  name: string;
  bank_name: string;
  account_type: string;
  account_number_masked: string | null;
  routing_number_masked: string | null;
  wire_routing_number_masked: string | null;
  wire_differs: boolean;
  gl_account_number: string;
  gl_account_name: string;
  chart_of_account_id: string;
  is_default_for_checks: boolean;
  receives_member_payments: boolean;
  is_payroll: boolean;
  next_check_number: number | null;
  check_stock_note: string | null;
  statement_day: number | null;
  is_active: boolean;
  closed_on: string | null;
  note: string | null;
  book_balance_minor: number;
  open_reconciliations?: number;
  reconciled_through?: string | null;
}

interface LedgerItem {
  journal_entry_line_id: string;
  journal_entry_id: string;
  entry_date: string;
  amount_minor: number;
  kind: string;
  reference: string | null;
  description: string | null;
  is_cleared: boolean;
  cleared_on: string | null;
}

interface Reconciliation {
  id: string;
  statement_date: string;
  opening_balance_minor: number;
  closing_balance_minor: number;
  status: string;
  completed_at: string | null;
  bank_account_name?: string;
}

interface Summary {
  bank_account_id: string;
  statement_date: string;
  status: string;
  opening_balance_minor: number;
  closing_balance_minor: number;
  cleared_minor: number;
  cleared_balance_minor: number;
  difference_minor: number;
  cleared_count: number;
  book_balance_minor: number;
  outstanding_check_minor: number;
  outstanding_check_count: number;
  in_transit_minor: number;
  in_transit_count: number;
  is_balanced: boolean;
}

const KIND_LABEL: Record<string, string> = {
  check: 'Check',
  deposit: 'Deposit',
  adjustment: 'Bank adjustment',
  other: 'Journal entry',
};

/* ================================================================== */
/* The list                                                            */
/* ================================================================== */

export function BankAccountsPage() {
  const { currency, atLeast } = useSession();
  const canEdit = atLeast('controller');
  const [showClosed, setShowClosed] = useState(false);
  const [adding, setAdding] = useState(false);

  const accounts = useApi<{ accounts: BankAccount[] }>(
    `/v1/banking/accounts?include_closed=${showClosed}`,
    [showClosed],
  );

  const rows = accounts.data?.accounts ?? [];

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <h1>Bank Accounts</h1>
          <div className="muted small">
            Where the club's money actually is, what it reconciles to, and which checkbook
            each one draws on.
          </div>
        </div>
        <div className="spacer" />
        <label
          className="muted small"
          style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
        >
          <input
            type="checkbox"
            checked={showClosed}
            onChange={(e) => setShowClosed(e.target.checked)}
          />
          Show closed
        </label>
        {canEdit && <button onClick={() => setAdding(true)}>Add an account</button>}
      </div>

      <ErrorBanner error={accounts.error} />

      {adding && (
        <NewAccountForm
          onDone={() => {
            setAdding(false);
            accounts.reload();
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      <Card flush>
        {accounts.loading ? (
          <div style={{ padding: 16 }}>
            <Loading rows={4} />
          </div>
        ) : rows.length === 0 ? (
          <Empty>This club has no bank accounts recorded yet.</Empty>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Account</th>
                <th>Bank</th>
                <th>Number</th>
                <th>Ledger account</th>
                <th className="num">Next check</th>
                <th className="num">Book balance</th>
                <th>Reconciled through</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr key={a.id} className={a.is_active ? undefined : 'muted'}>
                  <td>
                    <Link to={`/banking/accounts/${a.id}`}>{a.name}</Link>
                    <div className="muted small">
                      {[
                        a.is_default_for_checks ? 'check runs' : null,
                        a.receives_member_payments ? 'member payments' : null,
                        a.is_payroll ? 'payroll' : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || a.account_type.replace('_', ' ')}
                    </div>
                  </td>
                  <td>{a.bank_name}</td>
                  <td className="mono">{a.account_number_masked ?? '—'}</td>
                  <td className="mono small">
                    {a.gl_account_number} {a.gl_account_name}
                  </td>
                  <td className="num mono">{a.next_check_number ?? '—'}</td>
                  <td className="num">
                    <Money minor={a.book_balance_minor} currency={currency} signed />
                  </td>
                  <td className="small">
                    {a.reconciled_through ? (
                      fmtDate(a.reconciled_through)
                    ) : (
                      <Status tone="warning">never</Status>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </>
  );
}

/* ================================================================== */
/* One account                                                         */
/* ================================================================== */

export function BankAccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { currency, atLeast } = useSession();
  const navigate = useNavigate();
  const canReveal = atLeast('controller');

  const [revealed, setRevealed] = useState<{
    routing_number: string | null;
    wire_routing_number: string | null;
    account_number: string | null;
  } | null>(null);
  const [editing, setEditing] = useState(false);

  const detail = useApi<{
    account: BankAccount;
    activity: LedgerItem[];
    reconciliations: Reconciliation[];
  }>(`/v1/banking/accounts/${id}`, [id]);

  const reveal = useMutation(async () =>
    api<{ routing_number: string; wire_routing_number: string; account_number: string }>(
      `/v1/banking/accounts/${id}/reveal`,
      { method: 'POST', body: { purpose: 'viewed on the account screen' } },
    ),
  );

  const account = detail.data?.account;

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <div className="muted small">
            <Link to="/banking/accounts">Bank Accounts</Link>
          </div>
          <h1>{account?.name ?? '…'}</h1>
          <div className="muted small">{account?.bank_name}</div>
        </div>
        <div className="spacer" />
        {canReveal && account && (
          <>
            <button className="secondary" onClick={() => setEditing((e) => !e)}>
              {editing ? 'Cancel' : 'Edit'}
            </button>{' '}
            <button onClick={() => navigate(`/banking/reconciliations/new?account=${account.id}`)}>
              Reconcile a statement
            </button>
          </>
        )}
      </div>

      <ErrorBanner error={detail.error} />
      <ErrorBanner error={reveal.error} />

      {detail.loading || !account ? (
        <Card>
          <Loading rows={5} />
        </Card>
      ) : (
        <>
          <div className="tiles">
            <Tile
              label="Book balance"
              value={<Money minor={account.book_balance_minor} currency={currency} signed />}
              note={`Ledger account ${account.gl_account_number}`}
            />
            <Tile
              label="Next check"
              value={account.next_check_number ?? '—'}
              note={account.check_stock_note ?? 'The next number out of this account’s box'}
            />
            <Tile
              label="Reconciled through"
              value={
                account.reconciled_through ? fmtDate(account.reconciled_through) : 'Never'
              }
              note={
                account.reconciled_through ? undefined : 'No statement has been signed off yet'
              }
              tone={account.reconciled_through ? undefined : 'warning'}
            />
          </div>

          {editing && (
            <EditAccountForm
              account={account}
              onDone={() => {
                setEditing(false);
                setRevealed(null);
                detail.reload();
              }}
            />
          )}

          <Card title="The Numbers">
            <table className="data">
              <tbody>
                <tr>
                  <th style={{ width: 220 }}>Account number</th>
                  <td className="mono">
                    {revealed?.account_number ?? account.account_number_masked ?? '—'}
                  </td>
                </tr>
                <tr>
                  <th>Routing number</th>
                  <td className="mono">
                    {revealed?.routing_number ?? account.routing_number_masked ?? '—'}
                  </td>
                </tr>
                <tr>
                  <th>Wire routing number</th>
                  <td className="mono">
                    {revealed?.wire_routing_number ??
                      account.wire_routing_number_masked ??
                      '—'}
                    {account.wire_differs && (
                      <span className="muted small"> · different from the ACH number</span>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>

            {canReveal && !revealed && (
              <div style={{ marginTop: 12 }}>
                <button
                  className="secondary"
                  disabled={reveal.busy}
                  onClick={async () => {
                    const r = await reveal.run();
                    if (r) setRevealed(r);
                  }}
                >
                  Show the full numbers
                </button>
                <div className="muted small" style={{ marginTop: 6 }}>
                  Every read is recorded against your name, with the reason. Anyone can look;
                  the record is what keeps it honest.
                </div>
              </div>
            )}
            {revealed && (
              <div style={{ marginTop: 12 }}>
                <Status tone="warning">This read has been recorded against your name.</Status>{' '}
                <button className="secondary small" onClick={() => setRevealed(null)}>
                  Hide
                </button>
              </div>
            )}
          </Card>

          <Card
            title="Statements"
            actions={
              <span className="muted small">
                {account.statement_day
                  ? `Cuts on the ${account.statement_day}${ordinal(account.statement_day)}`
                  : null}
              </span>
            }
            flush
          >
            {detail.data!.reconciliations.length === 0 ? (
              <Empty>Nothing has been reconciled on this account yet.</Empty>
            ) : (
              <table className="data">
                <thead>
                  <tr>
                    <th>Statement date</th>
                    <th className="num">Opening</th>
                    <th className="num">Closing</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data!.reconciliations.map((r) => (
                    <tr key={r.id}>
                      <td>
                        <Link to={`/banking/reconciliations/${r.id}`}>
                          {fmtDate(r.statement_date)}
                        </Link>
                      </td>
                      <td className="num">
                        <Money minor={r.opening_balance_minor} currency={currency} signed />
                      </td>
                      <td className="num">
                        <Money minor={r.closing_balance_minor} currency={currency} signed />
                      </td>
                      <td>
                        {r.status === 'completed' ? (
                          <Status tone="good">completed {dateTime(r.completed_at)}</Status>
                        ) : (
                          <Status tone="warning">{r.status}</Status>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card title="Recent Activity" flush>
            {detail.data!.activity.length === 0 ? (
              <Empty>Nothing has moved through this account.</Empty>
            ) : (
              <table className="data">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>What</th>
                    <th>Reference</th>
                    <th className="num">Amount</th>
                    <th>Cleared</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data!.activity.map((i) => (
                    <tr key={i.journal_entry_line_id}>
                      <td>{fmtDate(i.entry_date)}</td>
                      <td>
                        {KIND_LABEL[i.kind] ?? i.kind}
                        {i.description && (
                          <span className="muted small"> · {i.description}</span>
                        )}
                      </td>
                      <td className="mono small">{i.reference ?? '—'}</td>
                      <td className="num">
                        <Money minor={i.amount_minor} currency={currency} signed />
                      </td>
                      <td className="small">
                        {i.is_cleared ? fmtDate(i.cleared_on) : (
                          <span className="muted">outstanding</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </>
      )}
    </>
  );
}

function ordinal(n: number): string {
  if (n % 100 >= 11 && n % 100 <= 13) return 'th';
  return ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
}

/* ================================================================== */
/* Forms                                                               */
/* ================================================================== */

interface LedgerAccount {
  id: string;
  account_number: string;
  name: string;
  already_used: boolean;
  used_by: string | null;
}

function NewAccountForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [bankName, setBankName] = useState('');
  const [glAccount, setGlAccount] = useState('');
  const [routing, setRouting] = useState('');
  const [accountNumber, setAccountNumber] = useState('');

  const coa = useApi<{ accounts: LedgerAccount[] }>('/v1/banking/ledger-accounts');
  const create = useMutation(async (body: Record<string, unknown>) =>
    api('/v1/banking/accounts', { method: 'POST', body }),
  );

  // Only postable asset accounts are offered, because BANK-1 refuses
  // anything else and a form that offers what the database will refuse
  // produces an error somebody has to interpret. The ones already spoken
  // for are shown and disabled rather than hidden: a controller who
  // cannot find "Cash — Operating" will make a second one.
  const cash = coa.data?.accounts ?? [];

  return (
    <Card title="New Bank Account">
      <ErrorBanner error={create.error} />
      <div className="row">
        <label>
          <span className="muted small">What the club calls it</span>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Operating" />
        </label>
        <label>
          <span className="muted small">Bank</span>
          <input
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            placeholder="Rockford Savings Bank"
          />
        </label>
        <label>
          <span className="muted small">Reconciles to</span>
          <select value={glAccount} onChange={(e) => setGlAccount(e.target.value)}>
            <option value="">Choose a ledger account…</option>
            {cash.map((a) => (
              <option key={a.id} value={a.id} disabled={a.already_used}>
                {a.account_number} — {a.name}
                {a.already_used ? ` (already ${a.used_by})` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>
      <div className="row" style={{ marginTop: 10 }}>
        <label>
          <span className="muted small">Routing number</span>
          <input
            value={routing}
            onChange={(e) => setRouting(e.target.value.replace(/\D/g, '').slice(0, 9))}
            placeholder="9 digits"
            className="mono"
          />
        </label>
        <label>
          <span className="muted small">Account number</span>
          <input
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            placeholder="Shown as ****6789 afterwards"
            className="mono"
          />
        </label>
      </div>
      <div className="muted small" style={{ marginTop: 8 }}>
        Once saved, these are masked everywhere. A controller can read them back, and every
        read is recorded.
      </div>
      <div style={{ marginTop: 12 }}>
        <button
          disabled={create.busy || !name || !bankName || !glAccount}
          onClick={async () => {
            const r = await create.run({
              name,
              bank_name: bankName,
              chart_of_account_id: glAccount,
              routing_number: routing || null,
              account_number: accountNumber || null,
            });
            if (r) onDone();
          }}
        >
          Save
        </button>{' '}
        <button className="secondary" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </Card>
  );
}

function EditAccountForm({ account, onDone }: { account: BankAccount; onDone: () => void }) {
  const [bankName, setBankName] = useState(account.bank_name);
  const [routing, setRouting] = useState('');
  const [wire, setWire] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [nextCheck, setNextCheck] = useState(
    account.next_check_number === null ? '' : String(account.next_check_number),
  );
  const [stockNote, setStockNote] = useState(account.check_stock_note ?? '');

  const save = useMutation(async (body: Record<string, unknown>) =>
    api(`/v1/banking/accounts/${account.id}`, { method: 'PATCH', body }),
  );

  return (
    <Card title={`Edit ${account.name}`}>
      <ErrorBanner error={save.error} />
      <div className="row">
        <label>
          <span className="muted small">Bank</span>
          <input value={bankName} onChange={(e) => setBankName(e.target.value)} />
        </label>
        <label>
          <span className="muted small">
            Next check number
          </span>
          <input
            className="mono"
            value={nextCheck}
            onChange={(e) => setNextCheck(e.target.value.replace(/\D/g, ''))}
            placeholder="15001"
          />
        </label>
        <label style={{ flex: 2 }}>
          <span className="muted small">Note about the stock</span>
          <input
            value={stockNote}
            onChange={(e) => setStockNote(e.target.value)}
            placeholder="New box opened 12 August, starts at 15001"
          />
        </label>
      </div>

      <div className="row" style={{ marginTop: 10 }}>
        <label>
          <span className="muted small">Routing number</span>
          <input
            className="mono"
            value={routing}
            onChange={(e) => setRouting(e.target.value.replace(/\D/g, '').slice(0, 9))}
            placeholder={account.routing_number_masked ?? '9 digits'}
          />
        </label>
        <label>
          <span className="muted small">Wire routing number</span>
          <input
            className="mono"
            value={wire}
            onChange={(e) => setWire(e.target.value.replace(/\D/g, '').slice(0, 9))}
            placeholder={account.wire_routing_number_masked ?? 'often not the same'}
          />
        </label>
        <label>
          <span className="muted small">Account number</span>
          <input
            className="mono"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            placeholder={account.account_number_masked ?? 'not recorded'}
          />
        </label>
      </div>
      <div className="muted small" style={{ marginTop: 8 }}>
        Leave a number blank to keep the one already there. They cannot be read back from this
        form — use “Show the full numbers”, which is recorded.
      </div>

      <div style={{ marginTop: 12 }}>
        <button
          disabled={save.busy}
          onClick={async () => {
            const body: Record<string, unknown> = { bank_name: bankName };
            if (routing) body.routing_number = routing;
            if (wire) body.wire_routing_number = wire;
            if (accountNumber) body.account_number = accountNumber;
            body.next_check_number = nextCheck ? Number(nextCheck) : null;
            body.check_stock_note = stockNote || null;
            const r = await save.run(body);
            if (r) onDone();
          }}
        >
          Save
        </button>
      </div>
    </Card>
  );
}

/* ================================================================== */
/* The reconciliation                                                  */
/* ================================================================== */

export function ReconciliationPage() {
  const { id } = useParams<{ id: string }>();
  const { currency, atLeast } = useSession();
  const canWork = atLeast('controller');

  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importing, setImporting] = useState(false);
  const [importText, setImportText] = useState('');
  const [note, setNote] = useState<string | null>(null);

  const detail = useApi<{
    summary: Summary;
    items: (LedgerItem & { cleared_here: boolean })[];
    statement_lines: {
      id: string;
      row_number: number;
      posted_on: string;
      description: string | null;
      amount_minor: number;
      check_number: number | null;
      match_kind: string | null;
      matched_line_id: string | null;
    }[];
  }>(`/v1/banking/reconciliations/${id}`, [id]);

  const clear = useMutation(async (lines: string[]) =>
    api(`/v1/banking/reconciliations/${id}/clear`, {
      method: 'POST',
      body: { journal_entry_line_ids: lines },
    }),
  );
  const unclear = useMutation(async (lines: string[]) =>
    api(`/v1/banking/reconciliations/${id}/unclear`, {
      method: 'POST',
      body: { journal_entry_line_ids: lines },
    }),
  );
  const complete = useMutation(async () =>
    api(`/v1/banking/reconciliations/${id}/complete`, { method: 'POST' }),
  );
  const runImport = useMutation(async (content: string) =>
    api<{ imported: number; already_present: number; problems: { message: string }[] }>(
      `/v1/banking/reconciliations/${id}/import`,
      { method: 'POST', body: { content, match: true } },
    ),
  );
  const clearMatched = useMutation(async () =>
    api<{ cleared: number }>(`/v1/banking/reconciliations/${id}/match`, {
      method: 'POST',
      body: { clear_matched: true },
    }),
  );

  const s = detail.data?.summary;
  const items = detail.data?.items ?? [];
  const done = s?.status === 'completed';

  // A selection can hold both kinds, and the two actions mean opposite
  // things. Sending a line that is already ticked off to the clear
  // endpoint gets a conflict from BANK-7 — correctly, but the operator
  // did not ask for anything wrong, so the screen splits the selection
  // rather than making them deselect by hand.
  const clearedIds = new Set(items.filter((i) => i.is_cleared).map((i) => i.journal_entry_line_id));
  const toTick = [...selected].filter((l) => !clearedIds.has(l));
  const toUntick = [...selected].filter((l) => clearedIds.has(l));

  const toggle = (line: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(line)) next.delete(line);
      else next.add(line);
      return next;
    });
  };

  const refresh = (message?: string | null) => {
    setSelected(new Set());
    setNote(message ?? null);
    detail.reload();
  };

  return (
    <>
      <div className="row" style={{ marginBottom: 16 }}>
        <div>
          <div className="muted small">
            <Link to="/banking/accounts">Bank Accounts</Link>
          </div>
          <h1>Statement of {s ? fmtDate(s.statement_date) : '…'}</h1>
          <div className="muted small">
            Tick off what the bank has seen. What is left is outstanding checks and deposits in
            transit.
          </div>
        </div>
        <div className="spacer" />
        {canWork && !done && (
          <>
            <button className="secondary" onClick={() => setImporting((v) => !v)}>
              Import a statement
            </button>{' '}
            <button
              disabled={!s?.is_balanced || complete.busy}
              title={
                s?.is_balanced
                  ? undefined
                  : 'The cleared balance does not equal the statement’s closing balance.'
              }
              onClick={async () => {
                const r = await complete.run();
                if (r) refresh('Signed off.');
              }}
            >
              Complete
            </button>
          </>
        )}
      </div>

      <ErrorBanner error={detail.error} />
      <ErrorBanner error={clear.error} />
      <ErrorBanner error={unclear.error} />
      <ErrorBanner error={complete.error} />
      <ErrorBanner error={runImport.error} />

      {note && (
        <Card>
          <Status tone="good">{note}</Status>
        </Card>
      )}

      {detail.loading || !s ? (
        <Card>
          <Loading rows={5} />
        </Card>
      ) : (
        <>
          <div className="tiles">
            <Tile
              label="Statement closes at"
              value={<Money minor={s.closing_balance_minor} currency={currency} signed />}
              note={`Opened at ${fmtMoney(s.opening_balance_minor, currency.exponent)}`}
            />
            <Tile
              label="Cleared balance"
              value={<Money minor={s.cleared_balance_minor} currency={currency} signed />}
              note={`${s.cleared_count} item${s.cleared_count === 1 ? '' : 's'} ticked off`}
            />
            <Tile
              label="Difference"
              value={<Money minor={s.difference_minor} currency={currency} signed />}
              note={
                s.is_balanced
                  ? 'Balanced'
                  : 'Find this before signing off — the ledger will refuse otherwise'
              }
              tone={s.is_balanced ? 'good' : 'critical'}
            />
          </div>

          {importing && canWork && (
            <Card title="Paste the Statement">
              <div className="muted small" style={{ marginBottom: 8 }}>
                CSV or an OFX/QFX download. Whichever it is is worked out from the content, so a
                renamed file is fine. A withdrawal comes in as a negative amount however the
                bank labels its columns.
              </div>
              <textarea
                rows={8}
                style={{ width: '100%', fontFamily: 'var(--mono, monospace)' }}
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder={'Date,Description,Check Number,Debit,Credit\n09/04/2026,CHECK,20501,1000.00,'}
              />
              <div style={{ marginTop: 10 }}>
                <button
                  disabled={runImport.busy || importText.trim() === ''}
                  onClick={async () => {
                    const r = await runImport.run(importText);
                    if (r) {
                      setImporting(false);
                      setImportText('');
                      refresh(
                        `Imported ${r.imported} line${r.imported === 1 ? '' : 's'}` +
                          (r.already_present ? `, ${r.already_present} already there` : '') +
                          (r.problems.length ? `, ${r.problems.length} could not be read` : '') +
                          '.',
                      );
                    }
                  }}
                >
                  Import
                </button>{' '}
                <button className="secondary" onClick={() => setImporting(false)}>
                  Cancel
                </button>
              </div>
            </Card>
          )}

          {detail.data!.statement_lines.length > 0 && (
            <Card
              title="The Statement, as Imported"
              actions={
                canWork && !done ? (
                  <button
                    className="secondary small"
                    disabled={clearMatched.busy}
                    onClick={async () => {
                      const r = await clearMatched.run();
                      if (r) refresh(`Ticked off ${r.cleared}.`);
                    }}
                  >
                    Tick off everything matched
                  </button>
                ) : null
              }
              flush
            >
              <table className="data">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th className="num">Check</th>
                    <th className="num">Amount</th>
                    <th>Matched</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.data!.statement_lines.map((l) => (
                    <tr key={l.id}>
                      <td>{fmtDate(l.posted_on)}</td>
                      <td>{l.description ?? '—'}</td>
                      <td className="num mono">{l.check_number ?? '—'}</td>
                      <td className="num">
                        <Money minor={l.amount_minor} currency={currency} signed />
                      </td>
                      <td className="small">
                        {l.matched_line_id ? (
                          <Status tone="good">{(l.match_kind ?? '').replace(/_/g, ' ')}</Status>
                        ) : l.match_kind === 'ambiguous' ? (
                          <Status tone="warning">more than one candidate</Status>
                        ) : (
                          <Status tone="neutral">nothing in the ledger</Status>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}

          <Card
            title="What the Account Has Done"
            actions={
              canWork && !done && selected.size > 0 ? (
                <>
                  {toTick.length > 0 && (
                    <button
                      className="small"
                      disabled={clear.busy}
                      onClick={async () => {
                        const r = await clear.run(toTick);
                        if (r) refresh(null);
                      }}
                    >
                      Tick off {toTick.length}
                    </button>
                  )}{' '}
                  {toUntick.length > 0 && (
                    <button
                      className="secondary small"
                      disabled={unclear.busy}
                      onClick={async () => {
                        const r = await unclear.run(toUntick);
                        if (r) refresh(null);
                      }}
                    >
                      Untick {toUntick.length}
                    </button>
                  )}
                </>
              ) : null
            }
            flush
          >
            {items.length === 0 ? (
              <Empty>Nothing has moved through this account.</Empty>
            ) : (
              <table className="data">
                <thead>
                  <tr>
                    <th style={{ width: 32 }} />
                    <th>Date</th>
                    <th>What</th>
                    <th>Reference</th>
                    <th className="num">Amount</th>
                    <th>Cleared</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((i) => (
                    <tr key={i.journal_entry_line_id}>
                      <td>
                        <input
                          type="checkbox"
                          disabled={!canWork || done || (i.is_cleared && !i.cleared_here)}
                          checked={selected.has(i.journal_entry_line_id)}
                          onChange={() => toggle(i.journal_entry_line_id)}
                        />
                      </td>
                      <td>{fmtDate(i.entry_date)}</td>
                      <td>
                        {KIND_LABEL[i.kind] ?? i.kind}
                        {i.description && <span className="muted small"> · {i.description}</span>}
                      </td>
                      <td className="mono small">{i.reference ?? '—'}</td>
                      <td className="num">
                        <Money minor={i.amount_minor} currency={currency} signed />
                      </td>
                      <td className="small">
                        {i.is_cleared ? (
                          <Status tone="good">{fmtDate(i.cleared_on)}</Status>
                        ) : (
                          <span className="muted">outstanding</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card title="What the Two Sides Differ By">
            <table className="data">
              <tbody>
                <tr>
                  <th style={{ width: 340 }}>Balance per the statement</th>
                  <td className="num">
                    <Money minor={s.closing_balance_minor} currency={currency} signed />
                  </td>
                </tr>
                <tr>
                  <th>
                    Less outstanding checks
                    <span className="muted small"> · {s.outstanding_check_count}</span>
                  </th>
                  <td className="num">
                    <Money minor={s.outstanding_check_minor} currency={currency} signed />
                  </td>
                </tr>
                <tr>
                  <th>
                    Plus deposits in transit
                    <span className="muted small"> · {s.in_transit_count}</span>
                  </th>
                  <td className="num">
                    <Money minor={s.in_transit_minor} currency={currency} signed />
                  </td>
                </tr>
                <tr>
                  <th>
                    <strong>Balance per the books</strong>
                  </th>
                  <td className="num">
                    <strong>
                      <Money minor={s.book_balance_minor} currency={currency} signed />
                    </strong>
                  </td>
                </tr>
              </tbody>
            </table>
            <div className="muted small" style={{ marginTop: 8 }}>
              {s.is_balanced ? (
                <>
                  This is the page an auditor asks for. The three lines above add to the fourth,
                  which is what "reconciled" means.
                </>
              ) : (
                <>
                  These four lines will not add up until the difference above is zero — which is
                  the point of the difference. Something has been ticked off that the bank has
                  not seen, or the statement's closing balance is not what was typed in.
                </>
              )}
            </div>
          </Card>
        </>
      )}
    </>
  );
}

/** A local helper so a tile note can hold a plain string. */
function fmtMoney(minor: number, exponent: number): string {
  return (minor / 10 ** exponent).toLocaleString(undefined, {
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
  });
}

/* ================================================================== */
/* Starting one                                                        */
/* ================================================================== */

export function NewReconciliationPage() {
  const navigate = useNavigate();
  const { currency } = useSession();
  const accountParam = new URLSearchParams(window.location.search).get('account') ?? '';

  const accounts = useApi<{ accounts: BankAccount[] }>('/v1/banking/accounts');
  const [accountId, setAccountId] = useState(accountParam);
  const [statementDate, setStatementDate] = useState('');
  const [closing, setClosing] = useState('');

  const create = useMutation(async (body: Record<string, unknown>) =>
    api<{ summary: Summary }>('/v1/banking/reconciliations', { method: 'POST', body }),
  );

  return (
    <>
      <h1>Reconcile a Statement</h1>
      <div className="muted small" style={{ marginBottom: 16 }}>
        The opening balance is last statement's closing balance and is filled in for you — a
        figure keyed in by hand is keyed in wrong once a year.
      </div>

      <ErrorBanner error={create.error} />

      <Card>
        <div className="row">
          <label>
            <span className="muted small">Account</span>
            <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
              <option value="">Choose…</option>
              {(accounts.data?.accounts ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} — {a.account_number_masked ?? a.gl_account_number}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span className="muted small">Statement date</span>
            <input
              type="date"
              value={statementDate}
              onChange={(e) => setStatementDate(e.target.value)}
            />
          </label>
          <label>
            <span className="muted small">Closing balance</span>
            <input
              value={closing}
              onChange={(e) => setClosing(e.target.value)}
              placeholder="0.00"
              className="mono"
            />
          </label>
        </div>

        <div style={{ marginTop: 12 }}>
          <button
            disabled={create.busy || !accountId || !statementDate || closing.trim() === ''}
            onClick={async () => {
              const minor = parseMoney(closing, currency);
              if (minor === null) return;
              const r = await create.run({
                bank_account_id: accountId,
                statement_date: statementDate,
                closing_balance_minor: minor,
              });
              if (r) navigate(`/banking/accounts/${accountId}`);
            }}
          >
            Start
          </button>
        </div>
      </Card>
    </>
  );
}
